// Piezas compartidas entre la vista general de Carritos Holanda y el detalle
// por agencia: colores/lectura, agrupación por agencia, celdas y acciones de ticket.
import { Truck } from "lucide-react";
import { useState } from "react";

import {
  HOLANDA_DISPOSITIONS,
  type HolandaChannel,
  type HolandaReading,
  type HolandaSite,
  type HolandaState,
  type HolandaTicketBrief,
} from "@/api/types";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  useAttendTicket,
  useNoteTicket,
  useResolveTicket,
} from "@/hooks/useHolanda";

// Estados — etiqueta clara (dice QUÉ pasa, no solo el color) + descripción/acción.
// El estado de una toma caída depende del HORARIO (hay alguien en sitio o no):
// noche/finde=ALARMA · amanece=guardia · rutas=posible ruta · tarde=warning.
export const STATE_META: Record<
  HolandaState,
  { label: string; cell: string; badge: string; desc?: string }
> = {
  ACTIVO_2: {
    label: "2 carritos enfriando",
    cell: "bg-green-500",
    badge: "bg-green-500 text-white",
  },
  ACTIVO_1: {
    label: "1 carrito enfriando",
    cell: "bg-green-400",
    badge: "bg-green-500 text-white",
  },
  ALARMA: {
    label: "Apagado · ALARMA",
    cell: "bg-red-500",
    badge: "bg-red-600 text-white",
    desc: "Apagado sin nadie en sitio (madrugada o fin de semana). Riesgo de descongelamiento — atender ya.",
  },
  NOTIF_GUARDIA: {
    label: "Apagado · avisar guardia",
    cell: "bg-orange-500",
    badge: "bg-orange-500 text-white",
    desc: "Apagado al amanecer — informar a la guardia para que lo revise al llegar (7 AM).",
  },
  WARNING: {
    label: "Apagado · revisar",
    cell: "bg-amber-500",
    badge: "bg-amber-500 text-black",
    desc: "Apagado por la tarde, cuando las rutas ya no salen — revisar (la guardia sigue en sitio).",
  },
  VIGILANDO: {
    label: "Apagado · vigilando",
    cell: "bg-amber-400",
    badge: "bg-amber-400 text-black",
    desc: "Se apagó de noche/fin de semana. En espera por si vuelve a prender antes de alarmar.",
  },
  POSIBLE_RUTA: {
    label: "Posible salida a ruta",
    cell: "bg-sky-400",
    badge: "bg-sky-500 text-white",
    desc: "Se apagó en horario de rutas — probablemente salió a reparto. Informativo.",
  },
  OFFLINE: {
    label: "Sin carrito",
    cell: "bg-slate-200 dark:bg-slate-700",
    badge: "bg-slate-400 text-white",
  },
  // Morado y no gris: gris es "sin carrito", y confundirlos es justo el defecto
  // que este estado corrige. Un tablero sin señal no se está vigilando.
  SIN_DATO: {
    label: "Tablero sin señal",
    cell: "bg-purple-500",
    badge: "bg-purple-600 text-white",
    desc: "El medidor de este tablero dejó de transmitir: puede ser el router de la agencia o que lo desconectaran por dentro. Mientras tanto sus carritos NO se están vigilando.",
  },
};

// Diferenciación de la lectura (cuando NO está en alerta).
export const READING_META: Record<
  HolandaReading,
  { label: string; cell: string }
> = {
  dos_carritos: { label: "2 carritos", cell: "bg-green-600" },
  un_carrito: { label: "1 carrito", cell: "bg-green-400" },
  consumo_minimo: {
    label: "Consumo mínimo",
    cell: "bg-sky-300 dark:bg-sky-800",
  },
  cero: { label: "Sin carrito", cell: "bg-slate-200 dark:bg-slate-700" },
};

// Estados "caídos" (consumo cayó) — todos llevan color de estado en la celda.
const DROPPED_STATES: HolandaState[] = [
  "ALARMA",
  "NOTIF_GUARDIA",
  "WARNING",
  "VIGILANDO",
  "POSIBLE_RUTA",
];
// Los que requieren acción (van a la cola "a atender"). POSIBLE_RUTA es solo
// informativo (probablemente salió a ruta) → va a un bloque aparte.
const ATTENTION_STATES: HolandaState[] = [
  "ALARMA",
  "NOTIF_GUARDIA",
  "WARNING",
  "VIGILANDO",
];

// Color/etiqueta de la celda: por el estado caído si lo hay, si no por la lectura.
// Un episodio ya limpiado se muestra ATENUADO (no como alarma viva): salió de la
// cola/KPIs pero la celda sigue ahí para no "desaparecer" el carrito del tablero.
export function cellMeta(ch: HolandaChannel): { label: string; cell: string } {
  if (ch.parcial && !ch.cleared) {
    return { label: parcialLabel(ch), cell: STATE_META[ch.state].cell };
  }
  if (ch.cleared) {
    return {
      label: `Limpiado · ${STATE_META[ch.state].label}`,
      cell: "bg-slate-300 dark:bg-slate-600",
    };
  }
  if (DROPPED_STATES.includes(ch.state)) return STATE_META[ch.state];
  return READING_META[ch.reading];
}

// "Caído" (cualquier etiqueta de apagado) — pinta color de estado y muestra carrito.
export function isAlert(state: HolandaState): boolean {
  return DROPPED_STATES.includes(state);
}

// Requiere acción del operador (cola roja/naranja); excluye "posible ruta".
export function needsAttention(state: HolandaState): boolean {
  return ATTENTION_STATES.includes(state);
}

// Color del carrito por estado: verde activo · color de la etiqueta si está caído.
export const CART_COLOR: Record<HolandaState, string> = {
  ACTIVO_2: "text-green-600",
  ACTIVO_1: "text-green-600",
  ALARMA: "text-red-600",
  NOTIF_GUARDIA: "text-orange-500",
  WARNING: "text-amber-500",
  VIGILANDO: "text-amber-500",
  POSIBLE_RUTA: "text-sky-500",
  OFFLINE: "text-slate-400",
  SIN_DATO: "text-purple-500",
};

// Cuántos camioncitos mostrar. Manda la GRÁFICA (la API ya trae en cart_count
// lo que ella dice cuando lo sabe: 1 ó 2), estable en alerta → muestra los que
// se vigilan. No se usa ACTIVO_1/2: esos umbrales de watts no distinguen 1 de 2.
export function cartCount(ch: HolandaChannel): number {
  // SIN_DATO: el medidor no transmite, así que cualquier número sería inventado.
  // La celda ya lo dice por color y etiqueta; aquí no se pintan camioncitos.
  if (ch.state === "OFFLINE" || ch.state === "SIN_DATO") return 0;
  if (ch.parcial) return 2; // 1 de 2: se pintan los dos, uno de cada color
  if (isAlert(ch.state)) return Math.max(1, ch.cart_count); // ≥1 carrito caído
  return ch.cart_count; // 0 (sin carrito) · 1 · 2
}

// Etiqueta de "1 de 2 desconectado": la toma está caída, pero NO toda.
export const PARCIAL_LABEL = "1 de 2 desconectado · el otro sigue enfriando";
// En VIGILANDO / POSIBLE_RUTA uno de los dos puede estar en su descanso normal
// del compresor (~4 h): no se afirma "desconectado" hasta que pase la espera.
export const PARCIAL_VIGILANDO_LABEL =
  "1 de 2 apagado · vigilando (el otro enfría)";

export function parcialLabel(ch: { state: HolandaState }): string {
  return ch.state === "VIGILANDO" || ch.state === "POSIBLE_RUTA"
    ? PARCIAL_VIGILANDO_LABEL
    : PARCIAL_LABEL;
}

// 1-2 íconos de carrito coloreados por estado (vacío si no hay carrito). En
// "1 de 2" el primero va verde (sigue enfriando) y el segundo con el color del
// estado de la caída: así se ve de un vistazo que no se apagó todo.
export function CartIcons({
  ch,
  size = "size-4",
}: {
  ch: HolandaChannel;
  size?: string;
}) {
  const n = cartCount(ch);
  if (n === 0) return null;
  // Limpiado → camioncito gris (atenuado), no el color de alarma del estado.
  const color = ch.cleared ? "text-slate-400" : CART_COLOR[ch.state];
  const vivo = ch.cleared ? "text-slate-400" : CART_COLOR.ACTIVO_1;
  return (
    <span className="inline-flex gap-0.5" data-testid="carritos">
      {Array.from({ length: n }).map((_, i) => (
        <Truck
          key={i}
          className={`${size} ${ch.parcial && i === 0 ? vivo : color}`}
          strokeWidth={2.5}
          data-testid={ch.parcial && i === 0 ? "carrito-vivo" : "carrito"}
        />
      ))}
    </span>
  );
}

export function fmtDur(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}min` : `${h}h`;
}

// De dónde sale el número de carritos y si el padrón de posiciones (IMEI de la
// toma, hoy el mismo que trae Axelia) dice lo mismo. Sin carrito, nada.
export function padronTexto(ch: HolandaChannel): string | null {
  if (ch.carritos_grafica == null || cartCount(ch) === 0) return null;
  const base = `Gráfica: ${ch.carritos_grafica} carrito(s)`;
  if (ch.coincide_padron === true)
    return `${base} · coincide con el padrón (IMEI)`;
  if (ch.coincide_padron === false)
    return `${base} · el padrón (IMEI) dice ${ch.padron} — verificar en sitio`;
  return base;
}

function channelTitle(ch: HolandaChannel): string {
  const padron = padronTexto(ch);
  return (
    `Canal ${ch.channel_num} — ${cellMeta(ch).label}\n` +
    `${cartCount(ch)} carrito(s) · ${Math.round(ch.watts)} W · ` +
    `${fmtDur(ch.minutes_in_state)} en estado\n` +
    (padron ? `${padron}\n` : "") +
    ch.label
  );
}

export interface Agency {
  code: string;
  name: string;
  sites: HolandaSite[];
  carts: number;
  alarms: number;
  watching: number;
}

// Nombre de agencia más limpio: quita un número suelto al final
// ("0310-HOLANDA MONTERREY 4" → "0310-HOLANDA MONTERREY").
function cleanAgencyName(name: string): string {
  return name.replace(/\s+\d+$/, "").trim() || name;
}

// Consolida los tableros (device_gid) de la misma agencia en un solo grupo.
// Nombre = el más DESCRIPTIVO (más largo) entre sus tableros, para no quedarnos
// con una etiqueta rota tipo "0313" cuando otro tablero sí trae la ciudad
// ("0313-HELADOS HOLANDA CD OBREGÓN"). Orden = por código (0301→).
export function groupAgencies(sites: HolandaSite[]): Agency[] {
  const map = new Map<string, Agency>();
  for (const s of sites) {
    const name = cleanAgencyName(s.agency_name);
    let a = map.get(s.agency_code);
    if (!a) {
      a = {
        code: s.agency_code,
        name,
        sites: [],
        carts: 0,
        alarms: 0,
        watching: 0,
      };
      map.set(s.agency_code, a);
    } else if (name.length > a.name.length) {
      a.name = name;
    }
    a.sites.push(s);
    a.carts += s.carts;
    a.alarms += s.alarms;
    a.watching += s.watching;
  }
  return [...map.values()].sort((x, y) => x.code.localeCompare(y.code));
}

export function Legend() {
  const carts = [
    { c: "text-green-600", t: "enfriando" },
    { c: "text-red-600", t: "ALARMA (noche/finde)" },
    { c: "text-orange-500", t: "avisar guardia (amanece)" },
    { c: "text-amber-500", t: "revisar (tarde)" },
    { c: "text-sky-500", t: "posible ruta" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {carts.map((i) => (
        <span key={i.t} className="flex items-center gap-1">
          <Truck className={`size-3.5 ${i.c}`} strokeWidth={2.5} /> {i.t}
        </span>
      ))}
      <span className="flex items-center gap-1">
        <Truck className="size-3.5 text-green-600" strokeWidth={2.5} />
        <Truck className="size-3.5 text-red-600" strokeWidth={2.5} /> 1 de 2
        desconectado
      </span>
      <span className="flex items-center gap-1">
        <span className="size-4 rounded border border-border bg-card" /> sin
        carrito
      </span>
    </div>
  );
}

// Cuadrito de la vista general: recuadro con 1-2 carritos coloreados por estado
// (vacío si no hay carrito — consumo mínimo / sin carrito).
export function MiniCell({ ch }: { ch: HolandaChannel }) {
  return (
    <span
      title={channelTitle(ch)}
      className="flex h-6 min-w-6 items-center justify-center gap-px rounded border border-border bg-card px-0.5"
    >
      <CartIcons ch={ch} size="size-3.5" />
    </span>
  );
}

// Botones de atención del ticket (atender / nota / resolver) reutilizables.
// Nombre del tablero TAL COMO está en Emporia/Zabbix, sacado del label crudo
// ("Emporia: 0306-HOLANDA OAXACA TABLERO A canal 4 (...) — watts"). El dashboard
// `emporia-carrito` filtra los items de Zabbix por este nombre; el homologado
// ("0306-UNILEVER-OAXACA · Tablero A") no existe en Zabbix y da "No data".
// No se recortan espacios: Emporia tiene nombres con espacios dobles.
export function nombreEmporia(label: string | null | undefined): string | null {
  if (!label) return null;
  const sinPrefijo = label.replace(/^Emporia: /, "");
  const i = sinPrefijo.indexOf(" canal ");
  if (i <= 0) return null;
  return sinPrefijo.slice(0, i);
}

// Query de la pantalla del dashboard del carrito: `site` va a Grafana (nombre
// de Emporia), `nombre` sólo al título (homologado).
export function carritoDashboardQuery(
  deviceGid: string,
  channelNum: number,
  siteName?: string | null,
  label?: string | null,
): string {
  const params = new URLSearchParams({
    gid: deviceGid,
    ch: String(channelNum),
  });
  const site = nombreEmporia(label) ?? siteName;
  if (site) params.set("site", site);
  if (siteName) params.set("nombre", siteName);
  return params.toString();
}

export function TicketActions({
  deviceGid,
  channelNum,
  ticket,
  siteName,
  label,
}: {
  deviceGid: string;
  channelNum: number;
  ticket: HolandaTicketBrief | null;
  siteName?: string | null;
  // Label crudo del canal (de él sale el nombre que Grafana sí encuentra).
  label?: string | null;
}) {
  const attend = useAttendTicket();
  const note = useNoteTicket();
  const resolve = useResolveTicket();
  const [mode, setMode] = useState<null | "note" | "resolve">(null);
  const [draft, setDraft] = useState("");
  const [disposition, setDisposition] = useState("");

  const target = { device_gid: deviceGid, channel_num: channelNum };
  const busy = attend.isPending || note.isPending || resolve.isPending;
  const attending = ticket?.status === "en_atencion";

  const navigate = useNavigate();
  // Navega a la pantalla INTERNA de AROC que embebe el dashboard del carrito
  // (no saca al operador del portal; esa vista trae su boton "Regresar").
  const abrirDashboard = () => {
    navigate(
      `/holanda/tablero-carrito?${carritoDashboardQuery(deviceGid, channelNum, siteName, label)}`,
    );
  };

  const reset = () => {
    setMode(null);
    setDraft("");
    setDisposition("");
  };

  return (
    <div className="space-y-1">
      {ticket && (
        <div className="text-xs text-muted-foreground">
          {attending
            ? `🧑 En atención: ${ticket.attended_by}`
            : `Ticket ${ticket.status}`}
          {ticket.notes_count > 0 && ` · ${ticket.notes_count} nota(s)`}
        </div>
      )}
      {mode === "resolve" ? (
        // Limpiar = sacar la alarma de la cola CON un motivo (se ata al episodio
        // actual; si vuelve a caer, vuelve a alarmar). Exige elegir motivo.
        <div className="space-y-1">
          <select
            autoFocus
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
            className="h-8 w-full rounded-md border bg-background px-2 text-sm"
          >
            <option value="">Motivo de la limpieza…</option>
            {HOLANDA_DISPOSITIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Nota (opcional)"
              className="h-8 flex-1 rounded-md border bg-background px-2 text-sm"
            />
            <Button
              size="sm"
              disabled={busy || !disposition}
              onClick={() =>
                resolve.mutate(
                  { ...target, disposition, note: draft.trim() || undefined },
                  { onSuccess: reset },
                )
              }
            >
              Confirmar limpieza
            </Button>
            <Button size="sm" variant="ghost" onClick={reset} disabled={busy}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : mode === "note" ? (
        <div className="flex items-center gap-2">
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Escribe una nota…"
            className="h-8 flex-1 rounded-md border bg-background px-2 text-sm"
          />
          <Button
            size="sm"
            disabled={busy || !draft.trim()}
            onClick={() =>
              draft.trim() &&
              note.mutate(
                { ...target, text: draft.trim() },
                { onSuccess: reset },
              )
            }
          >
            Enviar
          </Button>
          <Button size="sm" variant="ghost" onClick={reset} disabled={busy}>
            Cancelar
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          {!attending && (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => attend.mutate(target)}
            >
              Atender
            </Button>
          )}
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => setMode("note")}
          >
            Nota
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={busy}
            onClick={() => setMode("resolve")}
          >
            Limpiar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            title="Abrir el detalle de este carrito en Grafana"
            onClick={abrirDashboard}
          >
            Ver dashboard
          </Button>
        </div>
      )}
    </div>
  );
}
